import type { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { ITEM_FOLLOW_SAVED, itemFollowErrorMessage } from '@modules/common/services/item-follow/item-follow-messages';
import { firstValueFrom, type Observable } from 'rxjs';
import { collectRailIds } from './notes-rail-selection';

export const RAIL_CLEAR_STOPPED = 'Stopped: you switched to another workspace.';

/** One kind of item a rail's Following row lists (notes, notebooks, tasks, projects, …) and how to unfollow it. */
export interface RailFollowKind {
  readonly list: Parameters<typeof collectRailIds>[1];
  readonly unfollow: (id: string) => Observable<unknown>;
  readonly record: (id: string) => void;
  readonly refresh: () => void;
}

class LeftTheTeam extends Error {}

/**
 * Team Following · Clear my subscriptions, for every app's rail: reads what the row lists, then unfollows those
 * items one by one in `tenant`. The clear belongs to the team it started in, so it stops the moment the person
 * moves to another workspace and stays stopped: the store counts every change of workspace, so before each list
 * page, before each unfollow and before it reports success the clear asks whether the workspace is still `tenant`
 * AND nothing changed since it started — going to another workspace and coming back (A → B → A) does not restart
 * it. What was already unfollowed stays; one message says what happened.
 */
export async function clearRailFollows(
  tenant: string,
  space: { contextId(): string | undefined; contextChanges(): number },
  kinds: readonly RailFollowKind[],
  toaster: ToasterService,
): Promise<void> {
  const startedAt = space.contextChanges();
  const stillHere = (): boolean => space.contextId() === tenant && space.contextChanges() === startedAt;
  let unfollowing = false;
  try {
    const lists: string[][] = [];
    for (const kind of kinds) {
      lists.push(
        await collectRailIds(tenant, (offset, limit) => {
          if (!stillHere()) throw new LeftTheTeam();
          return kind.list(offset, limit);
        }),
      );
    }
    if (!stillHere()) throw new LeftTheTeam();
    if (!lists.some((ids) => ids.length)) {
      toaster.show({ text: 'No items in this view.' });
      return;
    }
    try {
      for (const [index, kind] of kinds.entries()) {
        for (const id of lists[index]) {
          if (!stillHere()) throw new LeftTheTeam();
          unfollowing = true;
          await firstValueFrom(kind.unfollow(id));
          kind.record(id);
        }
      }
    } finally {
      // After a partial failure too: some follows are gone and the lists must say so.
      if (unfollowing) for (const kind of kinds) kind.refresh();
    }
    if (!stillHere()) throw new LeftTheTeam();
    toaster.show({ text: ITEM_FOLLOW_SAVED });
  } catch (error) {
    toaster.show({
      text: error instanceof LeftTheTeam || !stillHere() ? RAIL_CLEAR_STOPPED : itemFollowErrorMessage(error),
    });
  }
}
